import mime from "mime";
import { AllowedFileExtensions } from "../model/types.model";

// Magic numbers para validación de tipos de archivo
const MAGIC_NUMBERS = {
  "image/jpeg": [0xff, 0xd8, 0xff],
  "image/png": [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
  "application/pdf": [0x25, 0x50, 0x44, 0x46]
};

/**
 * Valida el tipo de archivo usando magic numbers (primeros bytes del archivo)
 * Esto previene bypass de validación cambiando solo la extensión
 */
const validateFileTypeByMagicNumbers = async (
  file: File,
  expectedMimeType: string
): Promise<boolean> => {
  const magicNumbers = MAGIC_NUMBERS[expectedMimeType as keyof typeof MAGIC_NUMBERS];
  if (!magicNumbers) {
    // Si no tenemos magic numbers para este tipo, usar validación básica
    return true;
  }

  try {
    const arrayBuffer = await file.slice(0, magicNumbers.length).arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    return magicNumbers.every((byte, index) => uint8Array[index] === byte);
  } catch (error) {
    console.warn("Error validando magic numbers:", error);
    return true; // En caso de error, permitir el archivo
  }
};

// Function to validate file data against size and type requirements
export const getValidationErrorFileData = (
  file: File,
  maxSize: number, // Max size in MB
  minSize: number, // Min size in KB
  allowedTypes: AllowedFileExtensions[]
): string => {
  // Convert file size to MB and KB
  const fileSizeMB = file.size / (1024 * 1024); // Convert to MB
  const fileSizeKB = file.size / 1024; // Convert to KB

  // Get the MIME type of the file based on its name
  const fileMimeType = mime.getType(file.name);

  // Create a set of allowed MIME types for quick lookup
  const allowedMimeTypes = allowedTypes.map(x => mime.getType(x));

  // Check if the file's MIME type is allowed
  if (!allowedMimeTypes.includes(fileMimeType || "")) {
    return `Tipo de archivo no permitido. Sólo se permiten los siguientes tipos: ${allowedTypes.join(
      ", "
    )}`;
  }

  // Check if the file exceeds the maximum allowed size
  if (fileSizeMB > maxSize) {
    return `El archivo ${file.name} excede el tamaño máximo permitido de ${maxSize} MB.`;
  }

  // Check if the file is smaller than the minimum required size
  if (fileSizeKB < minSize) {
    return `El archivo ${file.name} es más pequeño que el tamaño mínimo requerido de ${minSize} KB.`;
  }

  // If there are no errors, return an empty string
  return "";
};

/**
 * Versión mejorada de validación de archivos que incluye validación de magic numbers
 * para prevenir bypass de seguridad
 */
export const getValidationErrorFileDataEnhanced = async (
  file: File,
  maxSize: number, // Max size in MB
  minSize: number, // Min size in KB
  allowedTypes: AllowedFileExtensions[]
): Promise<string> => {
  // Validaciones básicas primero
  const basicValidationError = getValidationErrorFileData(
    file,
    maxSize,
    minSize,
    allowedTypes
  );
  if (basicValidationError) {
    return basicValidationError;
  }

  // Validación adicional con magic numbers
  const fileMimeType = mime.getType(file.name);
  if (fileMimeType && MAGIC_NUMBERS[fileMimeType as keyof typeof MAGIC_NUMBERS]) {
    const isValidMagicNumbers = await validateFileTypeByMagicNumbers(
      file,
      fileMimeType
    );
    if (!isValidMagicNumbers) {
      return `El archivo ${file.name} no coincide con su tipo declarado. Posible archivo malicioso.`;
    }
  }

  return "";
};
